import { useState } from 'react';

import Input from '@cartwhl/ui/atoms/Input';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import Button from '@cartwhl/ui/input/Button';
import PlayerDialog from '@cartwhl/ui/molecules/PlayerDialog';

import { useOpenWithMascotPrefill } from '../../AssetLibrary/hooks/useAssetLibraryOpenWithPrefill';
import { useStore, withProvider } from '../batchMascotUploadStore';
import type { MascotRow } from '../types';
import MascotRowSection from './MascotRowSection';
import RecentMascotJobs from './RecentMascotJobs';

function SceneCard({
  row,
  index,
  canRemove,
}: {
  row: MascotRow;
  index: number;
  canRemove: boolean;
}) {
  const store = useStore();
  const [promptLocal, setPromptLocal] = useState(row.prompt);

  return (
    <div className="group relative">
      {canRemove && (
        <button
          type="button"
          onClick={() => store.removeMascotRow(row.id)}
          className="bg-primary border-primary hover:bg-secondary absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full border opacity-100 shadow-sm transition-colors transition-opacity lg:pointer-events-none lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100"
          aria-label="Remove scene"
        >
          <CrossIcon className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="bg-primary border-primary flex flex-col gap-4 rounded-lg border px-5 py-6">
        <Text variant="studio/body/medium/md" className="text-primary">
          Scene {index + 1}
        </Text>

        <MascotRowSection row={row} />

        <div className="flex flex-col gap-2">
          <Text variant="studio/body/medium/sm" className="text-secondary">
            Scene prompt
          </Text>
          <Input
            as="textarea"
            name={`prompt-${row.id}`}
            placeholder="Ex. Replace the man with character 1 and replace the woman with character 2..."
            value={promptLocal}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const val = e.target.value;
              setPromptLocal(val);
              store.updateMascotRow(row.id, 'prompt', val);
            }}
            className="h-auto! min-h-[80px] w-full resize-none text-sm"
          />
        </div>
      </div>
    </div>
  );
}

function UploadScreen() {
  const store = useStore();
  useOpenWithMascotPrefill(store);
  const {
    batchName,
    mascotRows,
    uploadModalState,
    uploadedFilesCount,
    totalFilesCount,
  } = store.state;
  const [batchNameLocal, setBatchNameLocal] = useState(batchName);

  const canRunProcess =
    batchNameLocal.trim().length > 0 &&
    mascotRows.length > 0 &&
    mascotRows.every(
      (r) =>
        r.videoFileCount >= 1 &&
        r.characterRefFilesCount >= 1 &&
        r.prompt.trim().length > 0,
    );

  const getModalMessage = () => {
    switch (uploadModalState) {
      case 'getting_links':
        return 'Getting upload links...';
      case 'uploading':
        return `Uploading files (${uploadedFilesCount} of ${totalFilesCount})`;
      case 'starting_process':
        return 'Starting process...';
      default:
        return 'Preparing...';
    }
  };

  const isModalVisible = uploadModalState !== 'idle';

  return (
    <div className="flex flex-col">
      <div className="container mx-auto flex flex-col px-4 pt-12 pb-6 sm:px-6 lg:px-8">
        {/* Intro blurb */}
        <div className="mb-8 shrink-0">
          <Text
            variant="studio/body/md"
            className="text-secondary max-w-[602px]"
          >
            Replace people or characters in your reference video with your
            custom character. Add a reference video, character image(s), and a
            prompt describing the replacement.
          </Text>
        </div>

        <div className="bg-secondary rounded-2xl p-3 sm:p-4">
          <div className="bg-primary border-primary grid grid-cols-1 gap-0 overflow-hidden rounded-lg border lg:flex lg:h-[640px]">
            <div className="border-primary flex min-h-0 flex-col rounded-t-lg lg:w-[68%] lg:rounded-none lg:rounded-l-lg lg:border-r">
              <div className="flex shrink-0 items-center justify-between px-6 pt-6 pb-4 lg:px-8 lg:pt-8">
                <Text
                  variant="studio/body/medium/24"
                  className="text-primary"
                  as="h2"
                >
                  <span className="mr-2">1.</span>Animate your scenes
                </Text>
                <Button
                  variant="tertiary"
                  onClick={() => store.addMascotRow()}
                  className="text-blue shrink-0 text-sm"
                >
                  + Add new scene
                </Button>
              </div>

              <div className="bg-secondary mx-3 mb-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-lg p-4 [scrollbar-width:none] lg:mx-5 lg:mb-6 [&::-webkit-scrollbar]:hidden">
                {mascotRows.map((row, index) => (
                  <SceneCard
                    key={row.id}
                    row={row}
                    index={index}
                    canRemove={mascotRows.length > 1}
                  />
                ))}
              </div>
            </div>

            <div className="border-primary flex min-h-0 flex-col gap-6 overflow-y-auto border-t p-6 lg:w-[34%] lg:border-t-0 lg:p-8">
              <Text
                variant="studio/body/medium/24"
                className="text-primary"
                as="h2"
              >
                <span className="mr-2">2.</span>Review your output options
              </Text>

              <div className="flex flex-col gap-2">
                <Text
                  variant="studio/body/medium/sm"
                  className="text-secondary"
                >
                  Batch name
                </Text>
                <Input
                  required
                  name="batchName"
                  placeholder="Ex. Parrot fight sequence"
                  value={batchNameLocal}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value;
                    setBatchNameLocal(val);
                    store.updateBatchName(val);
                  }}
                />
              </div>

              <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-primary"
                  >
                    Output format
                  </Text>
                  <Text variant="studio/body/sm" className="text-secondary">
                    MP4
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-primary"
                  >
                    Output length
                  </Text>
                  <Text variant="studio/body/sm" className="text-secondary">
                    Trimmed to 300 frames at the output framerate
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-primary"
                  >
                    Aspect ratio
                  </Text>
                  <Text variant="studio/body/sm" className="text-secondary">
                    Defaults to the closest match to your input video:
                  </Text>
                  <ul className="text-secondary list-disc pl-4">
                    <Text variant="studio/body/sm" as="li">
                      16:9 (1280x736)
                    </Text>
                    <Text variant="studio/body/sm" as="li">
                      1:1 (736x736)
                    </Text>
                    <Text variant="studio/body/sm" as="li">
                      9:16 (736x1280)
                    </Text>
                  </ul>
                </div>
                <div className="flex flex-col gap-1">
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-primary"
                  >
                    Frame rate
                  </Text>
                  <ul className="text-secondary list-disc pl-4">
                    <Text variant="studio/body/sm" as="li">
                      Matches input frame rate
                    </Text>
                    <Text variant="studio/body/sm" as="li">
                      Non-MP4 will be converted to 30fps
                    </Text>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              size="lg"
              disabled={!canRunProcess}
              onClick={() => store.runProcess()}
            >
              Run process
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto mt-12 px-4 sm:px-6 lg:px-8">
        <div className="border-gray-30 dark:border-gray-75 border-t" />
      </div>
      <RecentMascotJobs />

      {/* Upload/Processing Modal */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <PlayerDialog maxWidth={280}>
            <div className="relative flex flex-col items-center pb-2">
              <div className="flex w-full flex-col items-center px-4">
                <Loading className="text-green h-12 w-12" />
                <Text variant="studio/body/sm" className="mt-5 text-center">
                  {getModalMessage()}
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-6"
                  onClick={() => store.cancelUpload()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </PlayerDialog>
        </div>
      )}
    </div>
  );
}

export const UploadScreenWithStore = withProvider(UploadScreen);
export default UploadScreen;
